// Phase SA-8 — Lazy-fetch leg rows for one autopsy.
//
// Hook is always called from <AutopsyHistoryRow>, but the actual
// network round-trip only fires when `enabled === true` (i.e. when
// the row is expanded). Avoids the conditional-hook anti-pattern
// while still being lazy.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import type { SlipAutopsyLegRow } from "./slipAutopsyLegs.ts";

export interface UseAutopsyLegsState {
  legs: SlipAutopsyLegRow[];
  loading: boolean;
}

export function useAutopsyLegs(
  autopsyId: string,
  enabled: boolean,
): UseAutopsyLegsState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [legs, setLegs] = useState<SlipAutopsyLegRow[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLegs = useCallback(async (): Promise<void> => {
    if (!user || !enabled || !autopsyId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("slip_autopsy_legs" as never)
        .select(
          // eslint-disable-next-line max-len
          "leg_id, autopsy_id, user_id, leg_order, fixture_name, market, selection, odds, outcome, confidence_score, confidence_tier, verdict, reasoning, learning_applied, was_in_strikza_pipeline, pattern_tags, created_at",
        )
        .eq("autopsy_id", autopsyId)
        .order("leg_order", { ascending: true });

      if (error) {
        // eslint-disable-next-line no-console
        console.warn("[useAutopsyLegs] fetch failed:", error.message);
        setLegs([]);
        setLoading(false);
        return;
      }
      setLegs((data ?? []) as unknown as SlipAutopsyLegRow[]);
      setLoading(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(
        "[useAutopsyLegs] threw:",
        e instanceof Error ? e.message : String(e),
      );
      setLegs([]);
      setLoading(false);
    }
  }, [supabase, user, enabled, autopsyId]);

  useEffect(() => {
    if (enabled) void fetchLegs();
  }, [enabled, fetchLegs]);

  return useMemo<UseAutopsyLegsState>(
    () => ({ legs, loading }),
    [legs, loading],
  );
}
